<template>
  <div class="modal fade" id="planDetailsModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-shield-check me-2"></i>
            Plan Details
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body" v-if="plan">
          <!-- Loading State for Dynamic Data -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading plan details...</span>
            </div>
          </div>

          <div v-else>
            <!-- Plan Header -->
            <div class="row mb-4">
              <div class="col-md-8">
                <div class="d-flex align-items-center mb-3">
                  <div class="avatar-lg me-3" :class="getPlanIconClass(plan.plan_type)">
                    <span class="avatar-title">
                      <i :class="getPlanIcon(plan.plan_type)" class="fs-2"></i>
                    </span>
                  </div>
                  <div>
                    <h4 class="mb-1">{{ plan.plan_name }}</h4>
                    <p class="text-muted mb-0">{{ plan.plan_code }}</p>
                    <span class="badge" :class="getPlanTypeBadgeClass(plan.plan_type)">
                      {{ formatPlanType(plan.plan_type) }}
                    </span>
                  </div>
                </div>
                <p class="text-muted">{{ plan.plan_description }}</p>
              </div>
              <div class="col-md-4">
                <div class="text-end">
                  <span class="badge fs-6 mb-2" :class="getStatusBadgeClass(plan.is_active)">
                    <i :class="getStatusIcon(plan.is_active)"></i>
                    {{ plan.is_active ? 'Active' : 'Inactive' }}
                  </span>
                  <p class="text-muted mb-0">
                    <small>Effective: {{ formatDate(plan.effective_from) }}</small>
                    <span v-if="plan.effective_to">
                      <br><small>Until: {{ formatDate(plan.effective_to) }}</small>
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <!-- Coverage Thresholds -->
            <div class="row mb-4" v-if="planThresholds">
              <div class="col-12">
                <h5 class="mb-3">
                  <i class="bx bx-cog me-2 text-warning"></i>
                  Coverage Thresholds
                  <span v-if="hasCustomThresholds" class="badge bg-info ms-2">
                    <i class="bx bx-customize me-1"></i>Customized
                  </span>
                </h5>
                <div class="row">
                  <div class="col-md-4">
                    <div class="card border-warning">
                      <div class="card-body text-center">
                        <i class="bx bx-clinic text-warning fs-1 mb-2"></i>
                        <h6 class="text-warning">Provider Threshold</h6>
                        <h4 class="text-warning mb-0">
                          ETB {{ formatCurrency(planThresholds.mandatory_provider_threshold?.value || 15000) }}
                        </h4>
                        <small class="text-muted">Must deliver below this</small>
                        <span v-if="planThresholds.mandatory_provider_threshold?.is_override"
                              class="badge bg-info mt-2">Override</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="card border-success">
                      <div class="card-body text-center">
                        <i class="bx bx-shield-check text-success fs-1 mb-2"></i>
                        <h6 class="text-success">Coverage Limit</h6>
                        <h4 class="text-success mb-0">
                          ETB {{ formatCurrency(planThresholds.full_coverage_upper_limit?.value || 250000) }}
                        </h4>
                        <small class="text-muted">Full coverage up to this</small>
                        <span v-if="planThresholds.full_coverage_upper_limit?.is_override"
                              class="badge bg-info mt-2">Override</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="card border-info">
                      <div class="card-body text-center">
                        <i class="bx bx-percentage text-info fs-1 mb-2"></i>
                        <h6 class="text-info">Coverage Rate</h6>
                        <h4 class="text-info mb-0">
                          {{ planThresholds.default_coverage_percentage?.value || 100 }}%
                        </h4>
                        <small class="text-muted">Default coverage</small>
                        <span v-if="planThresholds.default_coverage_percentage?.is_override"
                              class="badge bg-info mt-2">Override</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pricing Information -->
            <div class="row mb-4">
              <div class="col-12">
                <h5 class="mb-3">
                  <i class="bx bx-money me-2 text-success"></i>
                  Pricing Structure
                </h5>
                <div class="row">
                  <div class="col-md-6">
                    <div class="card border-success">
                      <div class="card-body text-center">
                        <i class="bx bx-user text-success fs-1 mb-2"></i>
                        <h5 class="text-success">Primary Member</h5>
                        <h3 class="text-success mb-0">ETB {{ formatCurrency(plan.primary_annual_amount) }}</h3>
                        <small class="text-muted">per year</small>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="card border-info">
                      <div class="card-body text-center">
                        <i class="bx bx-group text-info fs-1 mb-2"></i>
                        <h5 class="text-info">Each Dependent</h5>
                        <h3 class="text-info mb-0">
                          <span v-if="plan.dependent_annual_amount > 0">
                            ETB {{ formatCurrency(plan.dependent_annual_amount) }}
                          </span>
                          <span v-else class="text-success">FREE</span>
                        </h3>
                        <small class="text-muted">per year</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="alert alert-info mt-3" v-if="plan.max_free_dependents > 0">
                  <i class="bx bx-info-circle me-2"></i>
                  <strong>{{ plan.max_free_dependents }}</strong> dependents are covered at no additional cost.
                  <span v-if="plan.dependent_annual_amount > 0">
                    Additional dependents will be charged <strong>ETB {{ formatCurrency(plan.dependent_annual_amount) }}</strong> each.
                  </span>
                </div>
              </div>
            </div>

            <!-- Coverage Limits -->
            <div class="row mb-4">
              <div class="col-12">
                <h5 class="mb-3">
                  <i class="bx bx-shield me-2 text-primary"></i>
                  Coverage Limits
                </h5>
                <div class="row">
                  <div class="col-md-6">
                    <div class="d-flex align-items-center p-3 border rounded">
                      <div class="avatar-sm bg-soft-primary rounded-circle me-3">
                        <i class="bx bx-calendar-check text-primary fs-5"></i>
                      </div>
                      <div>
                        <h6 class="mb-0">Annual Coverage Limit</h6>
                        <h5 class="text-primary mb-0">ETB {{ formatCurrency(plan.annual_coverage_limit) }}</h5>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex align-items-center p-3 border rounded">
                      <div class="avatar-sm bg-soft-info rounded-circle me-3">
                        <i class="bx bx-plus-medical text-info fs-5"></i>
                      </div>
                      <div>
                        <h6 class="mb-0">Per Visit Limit</h6>
                        <h5 class="text-info mb-0">ETB {{ formatCurrency(plan.per_visit_limit) }}</h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Dynamic Covered Services -->
            <div class="row mb-4" v-if="coveredServices">
              <div class="col-12">
                <h5 class="mb-3">
                  <i class="bx bx-check-shield me-2 text-success"></i>
                  Covered Services ({{ totalCoveredServices }} Services)
                </h5>

                <!-- Service Categories Tabs -->
                <ul class="nav nav-tabs mb-3" role="tablist">
                  <li class="nav-item" v-for="(services, category) in coveredServices" :key="category">
                    <a class="nav-link" :class="{ active: isFirstCategory(category) }"
                       :href="`#category-${slugify(category)}`"
                       data-bs-toggle="tab" role="tab">
                      {{ category }}
                      <span class="badge bg-secondary ms-1">{{ services.length }}</span>
                    </a>
                  </li>
                </ul>

                <!-- Tab Content -->
                <div class="tab-content">
                  <div v-for="(services, category) in coveredServices" :key="category"
                       :id="`category-${slugify(category)}`"
                       class="tab-pane" :class="{ active: isFirstCategory(category) }">
                    <div class="row">
                      <div class="col-md-6" v-for="service in services" :key="service.service_id">
                        <div class="d-flex align-items-center p-2 border-bottom">
                          <i class="bx bx-check text-success me-2"></i>
                          <div class="flex-grow-1">
                            <strong>{{ service.service_name }}</strong>
                            <div class="d-flex gap-1 mt-1">
                              <span v-if="service.requires_preauth" class="badge bg-warning" title="Requires Pre-authorization">
                                <i class="bx bx-lock-alt"></i> Pre-auth
                              </span>
                              <span v-if="service.requires_referral" class="badge bg-info" title="Requires Referral">
                                <i class="bx bx-directions"></i> Referral
                              </span>
                              <span v-if="service.default_coverage_status === 'limited'" class="badge bg-secondary">
                                Limited
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Coverage Rules if Available -->
            <div class="row mb-4" v-if="coverageRules && coverageRules.length > 0">
              <div class="col-12">
                <h5 class="mb-3">
                  <i class="bx bx-list-check me-2 text-info"></i>
                  Coverage Rules
                </h5>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <thead>
                    <tr>
                      <th>Service/Category</th>
                      <th>Coverage %</th>
                      <th>Copayment</th>
                      <th>Annual Limit</th>
                      <th>Frequency</th>
                      <th>Waiting Period</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="rule in coverageRules" :key="rule.rule_id">
                      <td>{{ rule.service_name || rule.category_name }}</td>
                      <td><span class="badge bg-success">{{ rule.coverage_percentage }}%</span></td>
                      <td>{{ rule.copayment_amount ? formatCurrency(rule.copayment_amount) : '-' }}</td>
                      <td>{{ rule.annual_limit ? formatCurrency(rule.annual_limit) : '-' }}</td>
                      <td>{{ rule.frequency_limit || '-' }}</td>
                      <td>{{ rule.waiting_period_days ? `${rule.waiting_period_days} days` : '-' }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Pricing Examples -->
            <div class="row">
              <div class="col-12">
                <h5 class="mb-3">
                  <i class="bx bx-calculator me-2 text-warning"></i>
                  Pricing Examples
                </h5>
                <div class="table-responsive">
                  <table class="table table-bordered">
                    <thead class="table-light">
                    <tr>
                      <th>Family Size</th>
                      <th>Primary Member</th>
                      <th>Free Dependents</th>
                      <th>Paid Dependents</th>
                      <th class="text-end">Annual Premium</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="example in getPricingExamples()" :key="example.size">
                      <td><strong>{{ example.description }}</strong></td>
                      <td>ETB {{ formatCurrency(plan.primary_annual_amount) }}</td>
                      <td>{{ example.freeDependents }}</td>
                      <td>{{ example.paidDependents }}</td>
                      <td class="text-end">
                        <strong class="text-primary">ETB {{ formatCurrency(example.total) }}</strong>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Coverage Example Calculator -->
            <div class="row mt-4">
              <div class="col-12">
                <h5 class="mb-3">
                  <i class="bx bx-health me-2 text-info"></i>
                  Coverage Example Calculator
                </h5>
                <div class="row">
                  <div class="col-md-4">
                    <label class="form-label">Enter Claim Amount (ETB)</label>
                    <input type="number" class="form-control" v-model.number="testAmount"
                           min="0" step="100" placeholder="Enter amount">
                  </div>
                  <div class="col-md-8">
                    <div v-if="testAmount > 0" class="alert alert-light border">
                      <strong>Coverage Calculation:</strong>
                      <ul class="mb-0">
                        <li v-if="testAmount <= mandatoryThreshold">
                          Provider must deliver service (100% covered)
                          <br><strong>CBHI Pays: ETB {{ formatCurrency(testAmount) }}</strong>
                          <br><strong>Patient Pays: ETB 0</strong>
                        </li>
                        <li v-else-if="testAmount <= coverageLimit">
                          Within coverage limit ({{ coveragePercentage }}% covered)
                          <br><strong>CBHI Pays: ETB {{ formatCurrency(testAmount * coveragePercentage / 100) }}</strong>
                          <br><strong>Patient Pays: ETB {{ formatCurrency(testAmount * (100 - coveragePercentage) / 100) }}</strong>
                        </li>
                        <li v-else>
                          Above coverage limit
                          <br><strong>CBHI Pays: ETB {{ formatCurrency(coverageLimit) }}</strong>
                          <br><strong>Patient Pays: ETB {{ formatCurrency(testAmount - coverageLimit) }}</strong>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-1"></i>Close
          </button>
          <button type="button" class="btn btn-warning" @click="manageCoverage">
            <i class="bx bx-shield me-1"></i>Manage Coverage Rules
          </button>
          <button type="button" class="btn btn-primary" @click="editPlan">
            <i class="bx bx-edit me-1"></i>Edit Plan
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "PlanDetailsModal",
  props: {
    plan: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      testAmount: 0,
      coveredServices: {},
      coverageRules: [],
      planThresholds: {},
      totalCoveredServices: 0
    };
  },
  computed: {
    hasCustomThresholds() {
      if (!this.planThresholds) return false;
      return Object.values(this.planThresholds).some(config => config.is_override);
    },
    mandatoryThreshold() {
      return this.planThresholds?.mandatory_provider_threshold?.value || 15000;
    },
    coverageLimit() {
      return this.planThresholds?.full_coverage_upper_limit?.value || 250000;
    },
    coveragePercentage() {
      return this.planThresholds?.default_coverage_percentage?.value || 100;
    }
  },
  methods: {
    async openModal() {
      this.testAmount = 0;
      this.loading = true;

      const modal = new bootstrap.Modal(document.getElementById('planDetailsModal'));
      modal.show();

      // Load dynamic data
      await this.loadPlanDetails();
      this.loading = false;
    },

    async loadPlanDetails() {
      if (!this.plan) return;

      try {
        // Load covered services
        await this.loadCoveredServices();

        // Load coverage rules
        await this.loadCoverageRules();

        // Load plan thresholds
        await this.loadPlanThresholds();
      } catch (e) {
        console.error('Error loading plan details:', e);
      }
    },

    async loadCoveredServices() {
      const data = {
        "function": "getPlanCoveredServices",
        "planId": this.plan.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.coveredServices = res.data.data.services_by_category || {};
          this.totalCoveredServices = res.data.data.total_services || 0;
        }
      } catch (e) {
        console.error('Failed to load covered services:', e);
      }
    },

    async loadCoverageRules() {
      const data = {
        "function": "getPlanCoverageDetails",
        "planId": this.plan.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.coverageRules = res.data.data.coverage_rules || [];
        }
      } catch (e) {
        console.error('Failed to load coverage rules:', e);
      }
    },

    async loadPlanThresholds() {
      const data = {
        "function": "getCoverageConfigurations",
        "planId": this.plan.plan_id
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.planThresholds = res.data.data.configurations || {};
        }
      } catch (e) {
        console.error('Failed to load plan thresholds:', e);
      }
    },

    editPlan() {
      this.$emit('edit-plan', this.plan);
      const modal = bootstrap.Modal.getInstance(document.getElementById('planDetailsModal'));
      modal.hide();
    },

    manageCoverage() {
      this.$emit('manage-coverage', this.plan);
      const modal = bootstrap.Modal.getInstance(document.getElementById('planDetailsModal'));
      modal.hide();
    },

    isFirstCategory(category) {
      const categories = Object.keys(this.coveredServices);
      return categories.length > 0 && categories[0] === category;
    },

    slugify(text) {
      return text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '');
    },

    // UI Helper Methods (remain the same)
    getPlanIcon(planType) {
      const iconMap = {
        'basic': 'bx bx-shield',
        'standard': 'bx bx-shield-check',
        'premium': 'bx bx-shield-plus',
        'family': 'bx bx-group',
        'corporate': 'bx bx-buildings'
      };
      return iconMap[planType] || 'bx bx-shield';
    },

    getPlanIconClass(planType) {
      const classMap = {
        'basic': 'bg-soft-secondary',
        'standard': 'bg-soft-primary',
        'premium': 'bg-soft-success',
        'family': 'bg-soft-info',
        'corporate': 'bg-soft-warning'
      };
      return `avatar-lg rounded-circle ${classMap[planType] || 'bg-soft-secondary'}`;
    },

    getPlanTypeBadgeClass(planType) {
      const classMap = {
        'basic': 'bg-secondary',
        'standard': 'bg-primary',
        'premium': 'bg-success',
        'family': 'bg-info',
        'corporate': 'bg-warning'
      };
      return classMap[planType] || 'bg-secondary';
    },

    formatPlanType(planType) {
      return planType ? planType.charAt(0).toUpperCase() + planType.slice(1) : '';
    },

    getStatusBadgeClass(isActive) {
      return isActive ? 'bg-success' : 'bg-danger';
    },

    getStatusIcon(isActive) {
      return isActive ? 'bx bx-check me-1' : 'bx bx-x me-1';
    },

    getPricingExamples() {
      if (!this.plan) return [];

      const primaryAmount = parseFloat(this.plan.primary_annual_amount) || 0;
      const dependentAmount = parseFloat(this.plan.dependent_annual_amount) || 0;
      const maxFree = parseInt(this.plan.max_free_dependents) || 0;

      const examples = [
        { size: 1, description: 'Single Person', dependents: 0 },
        { size: 2, description: 'Couple', dependents: 1 },
        { size: 4, description: 'Family of 4', dependents: 3 },
        { size: 6, description: 'Family of 6', dependents: 5 }
      ];

      return examples.map(example => {
        const freeDependents = Math.min(example.dependents, maxFree);
        const paidDependents = Math.max(0, example.dependents - maxFree);
        const total = primaryAmount + (paidDependents * dependentAmount);

        return {
          ...example,
          freeDependents,
          paidDependents,
          total
        };
      });
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat().format(amount || 0);
    }
  }
};
</script>

<style scoped>
/* Previous styles remain the same */
.avatar-lg {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-sm {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-soft-primary {
  background-color: rgba(78, 115, 223, 0.1);
  color: #4e73df;
}

.bg-soft-secondary {
  background-color: rgba(133, 135, 150, 0.1);
  color: #858796;
}

.bg-soft-success {
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.bg-soft-info {
  background-color: rgba(23, 162, 184, 0.1);
  color: #17a2b8;
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.nav-tabs .nav-link {
  color: #495057;
}

.nav-tabs .nav-link.active {
  font-weight: 600;
}

.tab-content {
  padding-top: 1rem;
}
</style>